import { FC, useState, useMemo, KeyboardEvent, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  AlertTriangle,
  Users,
  Tag,
  Upload,
  Trash2,
  X,
  CircleDot,
  Check,
  Link2,
  User,
  FileText
} from 'lucide-react';
import clsx from 'clsx';
import { Button } from 'components/ui/Button';
import { NsKey } from 'enums/NsKey';
import {
  NoteListItemDto,
  NoteFormValues,
  TagNoteDto,
  PersonMinimalDto
} from '../types/interface';
import {
  formatNoteDate,
  mapListItemToFormValues,
  getInitialFormValues
} from '../mappers/noteMappers';
import { useNotePriorities } from '../hooks/useNotePriorities';
import { useNoteStatuses } from '../hooks/useNoteStatuses';
import { clientsAPI } from 'services/swagger-api';
import { ClientListItem } from 'api/clients';
import TagAutocomplete from './TagAutocomplete';

/**
 * Helper to lighten a hex color for background
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface NoteFormProps {
  note?: NoteListItemDto;
  onSave: (values: NoteFormValues) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const NoteForm: FC<NoteFormProps> = ({ note, onSave, onCancel, onDelete }) => {
  const { t } = useTranslation(NsKey.Note);
  const isEditMode = !!note;

  // Get priorities and statuses from referential store
  const { priorities } = useNotePriorities();
  const { statuses } = useNoteStatuses();

  // Find default options with code "AUCUNE"
  const defaultPriorityId = useMemo(
    () => priorities.find((p) => p.code === 'AUCUNE')?.id,
    [priorities]
  );
  const defaultStatusId = useMemo(
    () => statuses.find((s) => s.code === 'AUCUN')?.id,
    [statuses]
  );

  // Initialize form values from note or empty
  const initialValues = useMemo(
    () => (note ? mapListItemToFormValues(note) : getInitialFormValues()),
    [note]
  );

  const [title, setTitle] = useState(initialValues.title);
  const [content, setContent] = useState(initialValues.content);
  const [selectedPriorityId, setSelectedPriorityId] = useState<
    string | undefined
  >(note?.priority?.id);
  const [selectedStatusId, setSelectedStatusId] = useState<string | undefined>(
    note?.status?.id
  );

  // Set default values when priorities/statuses are loaded (only in create mode)
  useEffect(() => {
    if (!isEditMode && !selectedPriorityId && defaultPriorityId) {
      setSelectedPriorityId(defaultPriorityId);
    }
  }, [isEditMode, selectedPriorityId, defaultPriorityId]);

  useEffect(() => {
    if (!isEditMode && !selectedStatusId && defaultStatusId) {
      setSelectedStatusId(defaultStatusId);
    }
  }, [isEditMode, selectedStatusId, defaultStatusId]);
  const [tags, setTags] = useState<TagNoteDto[]>(initialValues.tags || []);
  const [mentions, setMentions] = useState<PersonMinimalDto[]>(
    initialValues.mentions || []
  );
  const [mentionInput, setMentionInput] = useState('');
  // Autocomplete mentions : clients du courtier connecté (route /clients/liste, auto-scopée)
  const [mentionResults, setMentionResults] = useState<ClientListItem[]>([]);
  const [isSearchingMentions, setIsSearchingMentions] = useState(false);
  const mentionSearchSeq = useRef(0);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const term = mentionInput.trim();
    if (term.length < 2) {
      setMentionResults([]);
      setIsSearchingMentions(false);
      return;
    }
    const seq = ++mentionSearchSeq.current;
    setIsSearchingMentions(true);
    const handle = setTimeout(async () => {
      try {
        const response = await clientsAPI.getClients({
          getGenericListCommandDto: { pageNumber: 1, pageSize: 6, searchString: term }
        });
        if (seq !== mentionSearchSeq.current) return;
        setMentionResults((response.items || []) as ClientListItem[]);
      } catch (error) {
        console.error('Recherche clients (mentions) impossible:', error);
        if (seq === mentionSearchSeq.current) setMentionResults([]);
      } finally {
        if (seq === mentionSearchSeq.current) setIsSearchingMentions(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [mentionInput]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = contentRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [content]);

  const generateInitials = (fullName?: string): string => {
    if (!fullName) return '??';
    return fullName.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  // Sélection d'un client dans l'autocomplete : on ajoute son VRAI personId à la note
  // (mentionPersonIds) — plus de saisie libre, uniquement des personnes existantes.
  const handleSelectMention = (client: ClientListItem) => {
    if (!mentions.some((m) => m.id === client.id)) {
      setMentions([
        ...mentions,
        {
          id: client.id,
          fullName: client.name,
          initials: generateInitials(client.name)
        } as PersonMinimalDto
      ]);
    }
    setMentionInput('');
    setMentionResults([]);
  };

  const handleMentionKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Entrée = sélectionner le premier résultat proposé
      if (mentionResults.length > 0) handleSelectMention(mentionResults[0]);
    }
  };

  const handleRemoveMention = (mentionId: string) => {
    setMentions(mentions.filter((m) => m.id !== mentionId));
  };

  const handleSubmit = () => {
    if (!title.trim()) return;

    const formValues: NoteFormValues = {
      title: title.trim(),
      content: content.trim(),
      priorityId: selectedPriorityId,
      statusId: selectedStatusId,
      tagIds: tags.map((t) => t.id).filter(Boolean) as string[],
      mentionPersonIds: mentions.map((m) => m.id).filter(Boolean) as string[],
      // Préserver l'existant à l'update : liens personne / dossier + pièces jointes
      // (sinon le back délie la note du client au premier enregistrement).
      attachmentIds: initialValues.attachmentIds || [],
      riskId: initialValues.riskId,
      personId: initialValues.personId,
      tags,
      mentions
    };

    onSave(formValues);
  };

  const isValid =
    title.trim().length > 0 && !!selectedPriorityId && !!selectedStatusId;
  const displayDate = note?.creationTime
    ? formatNoteDate(note.creationTime)
    : '';

  return (
    <div className="bg-white h-full overflow-y-auto">
      <div className="px-6 py-5">
        {/* Header with title input and action buttons */}
        <div className="flex items-start justify-between mb-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('FORM.TITLE_PLACEHOLDER')}
            className="text-[22px] font-semibold text-slate-800 bg-transparent border-none outline-none w-full placeholder:text-slate-300 focus:ring-0"
          />
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <Button outline color="primary" onClick={onCancel}>
              <X data-slot="icon" />
              {t('FORM.CANCEL')}
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={!isValid}>
              <Check data-slot="icon" />
              {isEditMode ? t('FORM.SAVE') : t('FORM.CREATE')}
            </Button>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-2 mb-5">
          {/* Date (read-only in edit mode) */}
          {isEditMode && displayDate && (
            <div className="flex items-center gap-3 h-8">
              <Clock size={15} className="text-slate-400" />
              <span className="text-[13px] text-slate-400 w-20">
                {t('DETAILS.DATE')}
              </span>
              <span className="text-[13px] text-slate-600">{displayDate}</span>
            </div>
          )}

          {/* Priority */}
          <div className="flex items-center gap-3 min-h-8">
            <AlertTriangle size={15} className="text-slate-400" />
            <span className="text-[13px] text-slate-400 w-20">
              {t('DETAILS.PRIORITY')}
            </span>
            <div className="flex items-center gap-2">
              {priorities.map((priority) => {
                const isSelected = selectedPriorityId === priority.id;
                const bgStyle = priority.color
                  ? { backgroundColor: hexToRgba(priority.color, isSelected ? 0.25 : 0.1) }
                  : undefined;
                const borderStyle = priority.color && isSelected
                  ? { borderColor: priority.color }
                  : undefined;

                return (
                  <button
                    key={priority.id}
                    type="button"
                    onClick={() => setSelectedPriorityId(priority.id)}
                    className={clsx(
                      'px-3 py-1 rounded-full text-xs font-medium transition-all border',
                      !priority.color && (isSelected
                        ? 'bg-slate-200 text-slate-600 border-slate-300'
                        : 'bg-slate-100 text-slate-600 border-transparent hover:border-slate-200')
                    )}
                    style={{
                      ...bgStyle,
                      ...borderStyle,
                      color: priority.color || undefined,
                      borderColor: isSelected ? priority.color : 'transparent'
                    }}
                  >
                    {priority.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3 min-h-8">
            <CircleDot size={15} className="text-slate-400" />
            <span className="text-[13px] text-slate-400 w-20">
              {t('DETAILS.STATUS')}
            </span>
            <div className="flex items-center gap-2">
              {statuses.map((status) => {
                const isSelected = selectedStatusId === status.id;
                const bgStyle = status.color
                  ? { backgroundColor: hexToRgba(status.color, isSelected ? 0.25 : 0.1) }
                  : undefined;

                return (
                  <button
                    key={status.id}
                    type="button"
                    onClick={() => setSelectedStatusId(status.id)}
                    className={clsx(
                      'px-3 py-1 rounded-full text-xs font-medium transition-all border',
                      !status.color && (isSelected
                        ? 'bg-slate-200 text-slate-600 border-slate-300'
                        : 'bg-slate-100 text-slate-600 border-transparent hover:border-slate-200')
                    )}
                    style={{
                      ...bgStyle,
                      color: status.color || undefined,
                      borderColor: isSelected ? status.color : 'transparent'
                    }}
                  >
                    {status.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rattachement direct (personne / contrat) — distinct des mentions : lecture
              seule, préservé à l'enregistrement */}
          {(note?.person?.id || note?.risk?.id) && (
            <div className="flex items-start gap-3 min-h-8">
              <Link2 size={15} className="text-slate-400 mt-1.5" />
              <span className="text-[13px] text-slate-400 w-20 mt-1">Rattachée à</span>
              <div className="flex flex-wrap items-center gap-2">
                {note?.person?.id && (
                  <span className="flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[13px] text-blue-700 ring-1 ring-inset ring-blue-100">
                    <User size={12} />
                    {note.person.fullName}
                  </span>
                )}
                {note?.risk?.id && (
                  <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[13px] text-emerald-700 ring-1 ring-inset ring-emerald-100">
                    <FileText size={12} />
                    {note.risk.identifier}
                    {note.risk.type && (
                      <span className="text-[11px] text-emerald-500">· {note.risk.type}</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Mentions */}
          <div className="flex items-start gap-3 min-h-8">
            <Users size={15} className="text-slate-400 mt-1.5" />
            <span className="text-[13px] text-slate-400 w-20 mt-1">
              {t('DETAILS.MENTIONS')}
            </span>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {mentions.map((mention) => (
                  <div
                    key={mention.id}
                    className="flex items-center gap-2 px-2.5 py-1 bg-slate-100 rounded-full"
                  >
                    <div className="w-5 h-5 rounded-full bg-slate-400 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-white">
                        {mention.initials || generateInitials(mention.fullName)}
                      </span>
                    </div>
                    <span className="text-[13px] text-slate-600">
                      {mention.fullName || ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveMention(mention.id || '')}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {/* Autocomplete : clients du courtier connecté (personId réel) */}
                <div className="relative">
                  <input
                    type="text"
                    value={mentionInput}
                    onChange={(e) => setMentionInput(e.target.value)}
                    onKeyDown={handleMentionKeyDown}
                    placeholder="Rechercher un client…"
                    className="h-7 px-2 rounded-md border border-slate-200 bg-white text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-primary transition-colors w-44"
                  />
                  {mentionInput.trim().length >= 2 && (
                    <div className="absolute left-0 top-full z-20 mt-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                      {isSearchingMentions ? (
                        <div className="px-3 py-2 text-xs text-slate-400">Recherche…</div>
                      ) : mentionResults.length > 0 ? (
                        mentionResults.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => handleSelectMention(client)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-primary/5"
                          >
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-400 text-[10px] font-medium text-white">
                              {generateInitials(client.name)}
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-xs font-medium text-slate-700">
                                {client.name}
                              </span>
                              {client.email && (
                                <span className="block truncate text-[11px] text-slate-400">
                                  {client.email}
                                </span>
                              )}
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-xs text-slate-400">
                          Aucun client trouvé
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="flex items-start gap-3 min-h-8">
            <Tag size={15} className="text-slate-400 mt-1.5" />
            <span className="text-[13px] text-slate-400 w-20 mt-1">
              {t('DETAILS.TAGS')}
            </span>
            <TagAutocomplete selectedTags={tags} onTagsChange={setTags} />
          </div>
        </div>

        {/* Body content */}
        <div className="mb-5">
          <textarea
            ref={contentRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('FORM.CONTENT_PLACEHOLDER')}
            rows={1}
            className="w-full text-[14px] leading-relaxed text-slate-500 whitespace-pre-wrap bg-transparent border-none outline-none p-0 placeholder:text-slate-300 resize-none overflow-hidden min-h-[120px] focus:ring-0"
          />
        </div>

        {/* Attachments count indicator (read-only in edit mode) */}
        {isEditMode &&
          note?.attachmentsCount &&
          note.attachmentsCount > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-2 text-slate-500">
                <Upload size={14} />
                <span className="text-[13px]">
                  {note.attachmentsCount} pièce(s) jointe(s)
                </span>
              </div>
            </div>
          )}

        {/* Add attachments dropzone */}
        <div className="mb-5">
          <div className="border-2 border-dashed border-slate-200 rounded-xl py-5 px-4 flex flex-col items-center justify-center cursor-pointer hover:border-slate-300 transition-colors">
            <Upload size={20} className="text-slate-400 mb-2" />
            <span className="text-[13px] text-slate-400">
              {t('DETAILS.ADD_ATTACHMENTS')}
            </span>
          </div>
        </div>

        {/* Delete action (only in edit mode) */}
        {isEditMode && onDelete && (
          <button
            onClick={onDelete}
            className="flex items-center gap-2 text-slate-400 hover:text-red-500 transition-colors"
          >
            <Trash2 size={14} />
            <span className="text-[13px]">{t('DETAILS.DELETE_NOTE')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default NoteForm;
